/**
 * Recursive CTE Compilation
 *
 * Compiles variable-length path traversals using WITH RECURSIVE.
 * Handles cycle detection and depth limiting using dialect-specific operations.
 */
import {
  CompilerInvariantError,
  UnsupportedPredicateError,
} from "../../errors";
import { IDENTITY_PATH_TOKEN_SEPARATOR } from "../../utils/path";
import { type FieldRef, type QueryAst, type SelectiveField } from "../ast";
import {
  type DialectAdapter,
  type DialectRecursiveQueryStrategy,
} from "../dialect";
import type { DatabaseExpression } from "../expressions";
import { compileOrderTerm, resolveNullOrdering } from "../order";
import { sql, type SqlFragment } from "../sql-fragment";
import { compileDatabaseExpression } from "./database-expressions";
import { emitRecursiveQuerySql } from "./emitter";
import {
  compileIdentityClassCte,
  planIdentityFrontierExpansion,
} from "./identity-traversal";
import { compileInverseTraversalDuplicateGuard } from "./inverse-traversal-guard";
import { compileLimitOffsetClauses } from "./limit-offset";
import {
  createTemporalFilterPass,
  runCompilerPass,
  runRecursiveTraversalSelectionPass,
  type TemporalFilterPass,
  type VariableLengthTraversal,
} from "./passes";
import { type LogicalPlan, lowerRecursiveQueryToLogicalPlan } from "./plan";
import { compileKindFilter as sharedCompileKindFilter } from "./predicate-utils";
import {
  assertRecursiveTraversalSupported,
  compileFieldValue,
  compileFieldValueFromColumn,
  compilePredicateExpression,
  type PredicateCompilerContext,
} from "./predicates";
import { assertRecordedQueryAstDoesNotUseCurrentIndexes } from "./recorded-current-index-guard";
import {
  markPredicateFieldsAsRequired,
  visitExpressionFields,
} from "./standard-pass-pipeline";
import { compileSelectivePropsExtraction } from "./typed-json-extract";
import {
  addRequiredColumn,
  EMPTY_REQUIRED_COLUMNS,
  mapSelectiveSystemFieldToColumn,
  markFieldRefAsRequired,
  markSelectiveFieldAsRequired,
  NODE_COLUMNS,
  quoteIdentifier,
  type RequiredColumnsByAlias,
  shouldProjectColumn,
} from "./utils";

// ============================================================
// Constants
// ============================================================

/**
 * Maximum depth for recursive CTE queries when maxDepth is unlimited (-1).
 *
 * Graphs with branching factor B produce O(B^depth) rows before cycle
 * detection can prune them. A default of 10 covers typical neighborhood,
 * shortest-path, and hierarchy use-cases without risking exponential blowup
 * on dense graphs. Users who need deeper traversals should pass
 * `.recursive({ maxHops: N })` explicitly (up to MAX_EXPLICIT_RECURSIVE_DEPTH).
 */
export const MAX_RECURSIVE_DEPTH = 10;

/**
 * Maximum depth for explicit maxDepth traversals.
 *
 * Explicit traversal bounds are opt-in and safe to allow at a higher ceiling
 * for stress testing and long-path workloads.
 */
export const MAX_EXPLICIT_RECURSIVE_DEPTH = 1000;

const NO_ALWAYS_REQUIRED_COLUMNS = new Set<string>();

/**
 * The per-step cycle/path token for a node table alias. For identity-expanded
 * traversals it is the composite `kind || SEP || id` so folded peers (same id,
 * different kind) are distinct tokens; otherwise it is the bare id, preserving
 * the exact pre-identity token and public path output.
 *
 * The separator is bound as a parameter (not `sql.raw`) so it needs no
 * dialect-specific `char()`/`chr()` builtin — both dialects concatenate bound
 * text with `||`. Composite tokens never reach the caller: result
 * materialization strips them back to bare ids with `stripIdentityPathTokens`.
 */
function compilePathToken(tableAlias: string, composite: boolean): SqlFragment {
  if (!composite) {
    return sql.raw(`${tableAlias}.id`);
  }
  // Parenthesized so dialect path builders that splice this fragment into a
  // larger `||` chain keep it one text operand — PostgreSQL's `text[] || x`
  // would otherwise re-associate and coerce the separator as an array literal.
  return sql`(${sql.raw(`${tableAlias}.kind`)} || ${IDENTITY_PATH_TOKEN_SEPARATOR} || ${sql.raw(`${tableAlias}.id`)})`;
}

type RecursiveQueryPassState = Readonly<{
  ast: QueryAst;
  ctx: PredicateCompilerContext;
  graphId: string;
  logicalPlan: LogicalPlan | undefined;
  requiredColumnsByAlias: RequiredColumnsByAlias | undefined;
  temporalFilterPass: TemporalFilterPass | undefined;
  traversal: VariableLengthTraversal | undefined;
}>;

function runRecursiveQueryPassPipeline(
  ast: QueryAst,
  graphId: string,
  ctx: PredicateCompilerContext,
): RecursiveQueryPassState {
  let state: RecursiveQueryPassState = {
    ast,
    ctx,
    graphId,
    logicalPlan: undefined,
    requiredColumnsByAlias: undefined,
    temporalFilterPass: undefined,
    traversal: undefined,
  };

  const recursiveTraversalPass = runCompilerPass(state, {
    name: "recursive_traversal",
    execute(currentState): VariableLengthTraversal {
      return runRecursiveTraversalSelectionPass(currentState.ast);
    },
    update(currentState, traversal): RecursiveQueryPassState {
      return {
        ...currentState,
        traversal,
      };
    },
  });
  state = recursiveTraversalPass.state;

  const temporalPass = runCompilerPass(state, {
    name: "temporal_filters",
    execute(currentState): TemporalFilterPass {
      return createTemporalFilterPass(
        currentState.ast,
        currentState.ctx.readInstant,
        currentState.ctx.recordedReadBinding,
      );
    },
    update(currentState, temporalFilterPass): RecursiveQueryPassState {
      return {
        ...currentState,
        temporalFilterPass,
      };
    },
  });
  state = temporalPass.state;

  assertRecordedQueryAstDoesNotUseCurrentIndexes(state.ast);

  const columnPruningPass = runCompilerPass(state, {
    name: "column_pruning",
    execute(currentState): RequiredColumnsByAlias | undefined {
      const traversal = currentState.traversal;
      if (traversal === undefined) {
        throw new CompilerInvariantError(
          "Recursive traversal pass did not select traversal",
        );
      }
      return collectRequiredColumnsByAlias(currentState.ast, traversal);
    },
    update(currentState, requiredColumnsByAlias): RecursiveQueryPassState {
      return {
        ...currentState,
        requiredColumnsByAlias,
      };
    },
  });
  state = columnPruningPass.state;

  const logicalPlanPass = runCompilerPass(state, {
    name: "logical_plan",
    execute(currentState): LogicalPlan {
      const loweringInput = {
        ast: currentState.ast,
        dialect: currentState.ctx.dialect.name,
        graphId: currentState.graphId,
        ...(currentState.traversal === undefined ?
          {}
        : { traversal: currentState.traversal }),
      };
      return lowerRecursiveQueryToLogicalPlan(loweringInput);
    },
    update(currentState, logicalPlan): RecursiveQueryPassState {
      return {
        ...currentState,
        logicalPlan,
      };
    },
  });
  state = logicalPlanPass.state;

  return state;
}

// ============================================================
// Main Compiler
// ============================================================

/**
 * Compiles a variable-length query using recursive CTEs.
 *
 * @param ast - The query AST
 * @param graphId - The graph ID
 * @param ctx - Predicate compiler context
 * @returns `SqlFragment` for the recursive query
 */
export function compileVariableLengthQuery(
  ast: QueryAst,
  graphId: string,
  ctx: PredicateCompilerContext,
): SqlFragment {
  assertRecursiveOutputSupported(ast);
  assertRecursiveTraversalSupported(ctx, "variable-length traversal");
  const strategy = ctx.dialect.capabilities.recursiveQueryStrategy;
  const handler = RECURSIVE_QUERY_STRATEGY_HANDLERS[strategy];
  return handler(ast, graphId, ctx);
}

/** Applies the shared output contract to single and composed recursion. */
export function assertRecursiveOutputSupported(ast: QueryAst): void {
  if (
    ast.groupBy !== undefined ||
    ast.having !== undefined ||
    (ast.aggregateOrderBy?.length ?? 0) > 0
  )
    throw new UnsupportedPredicateError(
      "Aggregating recursive traversals is not yet supported; project nodes as a relation before aggregating.",
    );
  const materialized = new Set(
    [
      ast.start.alias,
      ...ast.traversals.map((traversal) => traversal.nodeAlias),
    ].flatMap((alias) => NODE_COLUMNS.map((column) => `${alias}_${column}`)),
  );
  for (const field of ast.projection.fields) materialized.add(field.outputName);
  for (const field of ast.selectiveFields ?? [])
    materialized.add(field.outputName);
  for (const traversal of ast.traversals) {
    for (const alias of [
      traversal.variableLength?.depthAlias,
      traversal.variableLength?.pathAlias,
    ]) {
      if (alias === undefined) continue;
      if (materialized.has(alias)) {
        throw new UnsupportedPredicateError(
          `Recursive traversal output alias "${alias}" collides with another result column`,
        );
      }
      materialized.add(alias);
    }
  }
}

type RecursiveQueryStrategyHandler = (
  ast: QueryAst,
  graphId: string,
  ctx: PredicateCompilerContext,
) => SqlFragment;

const RECURSIVE_QUERY_STRATEGY_HANDLERS: Record<
  DialectRecursiveQueryStrategy,
  RecursiveQueryStrategyHandler
> = {
  recursive_cte: compileVariableLengthQueryWithRecursiveCteStrategy,
};

/** Compiles one traversal stage restricted to kind-qualified upstream seeds. */
export function compileRecursiveStage(
  ast: QueryAst,
  graphId: string,
  ctx: PredicateCompilerContext,
  seedQuery: SqlFragment,
): SqlFragment {
  assertRecursiveTraversalSupported(ctx, "variable-length traversal stage");
  return compileVariableLengthQueryWithRecursiveCteStrategy(
    ast,
    graphId,
    ctx,
    seedQuery,
  );
}

function compileVariableLengthQueryWithRecursiveCteStrategy(
  ast: QueryAst,
  graphId: string,
  ctx: PredicateCompilerContext,
  seedQuery?: SqlFragment,
): SqlFragment {
  const passState = runRecursiveQueryPassPipeline(ast, graphId, ctx);

  const { dialect } = ctx;
  const {
    logicalPlan,
    requiredColumnsByAlias,
    temporalFilterPass,
    traversal: vlTraversal,
  } = passState;

  if (temporalFilterPass === undefined) {
    throw new CompilerInvariantError(
      "Temporal filter pass did not initialize temporal state",
    );
  }
  if (logicalPlan === undefined) {
    throw new CompilerInvariantError(
      "Logical plan pass did not initialize plan state",
    );
  }
  if (vlTraversal === undefined) {
    throw new CompilerInvariantError(
      "Recursive traversal pass did not select traversal",
    );
  }

  // Build the recursive CTE
  const recursiveCte = compileRecursiveCte(
    ast,
    vlTraversal,
    graphId,
    ctx,
    requiredColumnsByAlias,
    temporalFilterPass,
    seedQuery,
  );

  // A historical read reconstructs its class relation from outside the recursive
  // term, so the ledger fixed point is evaluated once for the whole traversal
  // rather than per expanded (frontier row, edge) pair. A current read emits no
  // such relation: its step seeks the closure from the worktable row itself.
  const identityClassCte = compileIdentityClassCte({
    ast,
    ctx,
    graphId,
    temporalFilterPass,
  });

  // Build projection
  const projection = compileRecursiveProjection(ast, vlTraversal, ctx);

  // Build final SELECT
  const minDepth = vlTraversal.variableLength.minDepth;
  const resultClauses: SqlFragment[] = [];
  if (minDepth > 0) resultClauses.push(sql`depth >= ${minDepth}`);
  if (vlTraversal.variableLength.stopExpansion?.emitStopNode === false)
    resultClauses.push(sql`stop_reached = ${dialect.booleanLiteral(false)}`);
  if (ast.resultPredicate !== undefined)
    resultClauses.push(
      compilePredicateExpression(ast.resultPredicate, {
        ...ctx,
        resolveFieldCteAlias: () => ctx.recursiveResultAlias,
      }),
    );
  const depthFilter =
    resultClauses.length > 0 ?
      sql`WHERE ${sql.join(resultClauses, sql` AND `)}`
    : sql.raw("");

  // Order by and limit/offset
  const orderBy = compileRecursiveOrderBy(ast, ctx);
  const limitOffset = compileLimitOffset(ast, dialect);

  return emitRecursiveQuerySql({
    depthFilter,
    ...(limitOffset === undefined ? {} : { limitOffset }),
    logicalPlan,
    ...(orderBy === undefined ? {} : { orderBy }),
    ...(identityClassCte === undefined ?
      {}
    : { precedingCtes: [identityClassCte] }),
    projection,
    recursiveCte,
    ...(ctx.recursiveResultAlias === undefined ?
      {}
    : { resultAlias: ctx.recursiveResultAlias }),
  });
}

/**
 * Checks if a query contains variable-length traversals.
 */
export function hasVariableLengthTraversal(ast: QueryAst): boolean {
  return ast.traversals.some((t) => t.variableLength !== undefined);
}

// ============================================================
// Recursive CTE Generation
// ============================================================

/**
 * Compiles the recursive CTE for variable-length traversal.
 */
function compileRecursiveCte(
  ast: QueryAst,
  traversal: VariableLengthTraversal,
  graphId: string,
  ctx: PredicateCompilerContext,
  requiredColumnsByAlias: RequiredColumnsByAlias | undefined,
  temporalFilterPass: TemporalFilterPass,
  seedQuery?: SqlFragment,
): SqlFragment {
  const { dialect } = ctx;
  const startAlias = ast.start.alias;
  const startKinds = ast.start.kinds;
  const nodeAlias = traversal.nodeAlias;
  const directEdgeKinds = [...new Set(traversal.edgeKinds)];
  const inverseEdgeKinds =
    traversal.inverseEdgeKinds === undefined ?
      []
    : [...new Set(traversal.inverseEdgeKinds)];
  const forceWorktableOuterJoinOrder =
    dialect.capabilities.forceRecursiveWorktableOuterJoinOrder;
  const nodeKinds = traversal.nodeKinds;
  const previousNodeKinds = [...new Set([...startKinds, ...nodeKinds])];
  const direction = traversal.direction;
  const vl = traversal.variableLength;
  const shouldEnforceCycleCheck = vl.cyclePolicy !== "allow";
  const shouldTrackPath =
    shouldEnforceCycleCheck ||
    (vl.pathAlias !== undefined && vl.pathFormat !== "qualified");
  const recursiveJoinRequiredColumns = new Set<string>(["id"]);
  if (
    previousNodeKinds.length > 1 ||
    traversal.includeIdentityMembers === true
  ) {
    recursiveJoinRequiredColumns.add("kind");
  }
  const requiredStartColumns =
    requiredColumnsByAlias ?
      (requiredColumnsByAlias.get(startAlias) ?? EMPTY_REQUIRED_COLUMNS)
    : undefined;
  const requiredNodeColumns =
    requiredColumnsByAlias ?
      (requiredColumnsByAlias.get(nodeAlias) ?? EMPTY_REQUIRED_COLUMNS)
    : undefined;
  const startColumnsFromBase = compileNodeSelectColumnsFromTable(
    "n0",
    startAlias,
    requiredStartColumns,
    NO_ALWAYS_REQUIRED_COLUMNS,
  );
  const startColumnsFromRecursive = compileNodeSelectColumnsFromRecursiveRow(
    startAlias,
    requiredStartColumns,
    NO_ALWAYS_REQUIRED_COLUMNS,
  );
  const nodeColumnsFromBase = compileNodeSelectColumnsFromTable(
    "n0",
    nodeAlias,
    requiredNodeColumns,
    recursiveJoinRequiredColumns,
  );
  const nodeColumnsFromRecursive = compileNodeSelectColumnsFromTable(
    "n",
    nodeAlias,
    requiredNodeColumns,
    recursiveJoinRequiredColumns,
  );

  const startKindFilter = compileKindFilter(startKinds, "n0.kind");
  const nodeKindFilter = compileKindFilter(nodeKinds, "n.kind");

  const startTemporalFilter = temporalFilterPass.forAlias("n0");
  const edgeTemporalFilter = temporalFilterPass.forAlias("e");
  const nodeTemporalFilter = temporalFilterPass.forAlias("n");

  // Start predicates (with cteColumnPrefix "" for raw n0 columns)
  const startContext = { ...ctx, cteColumnPrefix: "" };
  const startPredicates = compileNodePredicates(ast, startAlias, startContext);

  // Edge predicates (with cteColumnPrefix "e" for e.props)
  const edgeContext = { ...ctx, cteColumnPrefix: "e" };
  const edgePredicates = compileEdgePredicates(
    ast,
    traversal.edgeAlias,
    edgeContext,
  );

  // Target node predicates (with cteColumnPrefix "n" for n.props)
  const targetContext = { ...ctx, cteColumnPrefix: "n" };
  const targetNodePredicates = compileNodePredicates(
    ast,
    nodeAlias,
    targetContext,
  );
  const stopExpression = traversal.variableLength.stopExpansion?.expression;
  const compiledStopExpression =
    stopExpression === undefined ? undefined : (
      compilePredicateExpression(stopExpression, targetContext)
    );
  const compiledBaseStopExpression =
    stopExpression === undefined ? undefined : (
      compilePredicateExpression(stopExpression, {
        ...ctx,
        cteColumnPrefix: "n0",
      })
    );

  // Max depth condition:
  // - unlimited traversals are capped at MAX_RECURSIVE_DEPTH
  // - explicit maxDepth traversals must not exceed MAX_EXPLICIT_RECURSIVE_DEPTH
  if (vl.maxDepth > MAX_EXPLICIT_RECURSIVE_DEPTH) {
    throw new UnsupportedPredicateError(
      `Recursive traversal maxHops(${vl.maxDepth}) exceeds maximum explicit depth of ${MAX_EXPLICIT_RECURSIVE_DEPTH}`,
    );
  }
  const effectiveMaxDepth = vl.maxDepth > 0 ? vl.maxDepth : MAX_RECURSIVE_DEPTH;
  const maxDepthCondition = sql`r.depth < ${effectiveMaxDepth}`;

  // Under identity folding two folded peers share an id but differ in kind, so
  // a bare-id path token treats a traversal that legitimately passes through
  // both peers as a revisit — a spurious cycle. Scope the fix to identity
  // traversals: their cycle-detection and path tokens become the composite
  // (kind, id), leaving every other traversal's token (and public path output)
  // byte-identical. See the `IdentityTraversalOption` JSDoc in
  // src/query/builder/query-builder.ts for the option's public semantics.
  const baseToken = compilePathToken(
    "n0",
    traversal.includeIdentityMembers === true,
  );
  const stepToken = compilePathToken(
    "n",
    traversal.includeIdentityMembers === true,
  );
  const cycleCheck =
    shouldEnforceCycleCheck ?
      dialect.cycleCheck(stepToken, sql.raw("r.path"))
    : undefined;
  const initialPath =
    shouldTrackPath ? dialect.initializePath(baseToken) : undefined;
  const pathExtension =
    shouldTrackPath ?
      dialect.extendPath(sql.raw("r.path"), stepToken)
    : undefined;

  // Base case WHERE clauses
  const baseWhereClauses = [
    sql`n0.graph_id = ${graphId}`,
    startKindFilter,
    startTemporalFilter,
    ...startPredicates,
  ];

  if (seedQuery !== undefined) {
    baseWhereClauses.push(
      sql`EXISTS (SELECT 1 FROM (${seedQuery}) AS __tg_seed WHERE __tg_seed.kind = n0.kind AND __tg_seed.id = n0.id)`,
    );
  }

  const previousIdColumn = sql`r.${sql.raw(nodeAlias)}_id`;
  const previousKindColumn = sql`r.${sql.raw(nodeAlias)}_kind`;
  const identityFrontierExpansion =
    traversal.includeIdentityMembers === true ?
      planIdentityFrontierExpansion({
        ast,
        ctx,
        graphId,
        previousId: previousIdColumn,
        previousKind: previousKindColumn,
        temporalFilterPass,
      })
    : undefined;

  const recursiveBaseWhereClauses: SqlFragment[] = [
    sql`e.graph_id = ${graphId}`,
    nodeKindFilter,
    edgeTemporalFilter,
    nodeTemporalFilter,
    maxDepthCondition,
    ...(stopExpression === undefined ?
      []
    : [
        sql`COALESCE(r.stop_reached, ${dialect.booleanLiteral(false)}) = ${dialect.booleanLiteral(false)}`,
      ]),
    // Conditions the frontier widening cannot state in a join condition —
    // currently the member-visibility guard. Every branch of the recursive term
    // carries them, because every branch reads the widened frontier.
    ...(identityFrontierExpansion?.whereClauses ?? []),
  ];
  if (cycleCheck !== undefined) {
    recursiveBaseWhereClauses.push(cycleCheck);
  }
  recursiveBaseWhereClauses.push(...edgePredicates, ...targetNodePredicates);

  /**
   * Connects a candidate edge to the row the worktable is expanding from. A
   * widened frontier and a plain one join the edge the same way — on the
   * worktable row's (kind, id) or on the class member's — so no traversal reaches
   * an edge through a correlated membership test.
   */
  function compileWorktableJoinClauses(
    branch: Readonly<{
      joinField: "from_id" | "to_id";
      joinKindField: "from_kind" | "to_kind";
    }>,
  ): SqlFragment[] {
    const edgeId = sql`e.${sql.raw(branch.joinField)}`;
    const edgeKind = sql`e.${sql.raw(branch.joinKindField)}`;
    if (identityFrontierExpansion !== undefined) {
      return [
        sql`${edgeId} = ${identityFrontierExpansion.memberId}`,
        sql`${edgeKind} = ${identityFrontierExpansion.memberKind}`,
      ];
    }
    const clauses = [sql`${edgeId} = ${previousIdColumn}`];
    if (previousNodeKinds.length > 1) {
      clauses.push(sql`${edgeKind} = ${previousKindColumn}`);
    }
    return clauses;
  }

  function compileRecursiveBranch(
    branch: Readonly<{
      joinField: "from_id" | "to_id";
      targetField: "from_id" | "to_id";
      joinKindField: "from_kind" | "to_kind";
      targetKindField: "from_kind" | "to_kind";
      edgeKinds: readonly string[];
      duplicateGuard?: SqlFragment | undefined;
    }>,
  ): SqlFragment {
    const recursiveFilterClauses = [
      ...recursiveBaseWhereClauses,
      compileKindFilter(branch.edgeKinds, "e.kind"),
      compileKindFilter(nodeKinds, `e.${branch.targetKindField}`),
    ];
    if (traversal.includeIdentityMembers !== true) {
      recursiveFilterClauses.push(
        compileKindFilter(previousNodeKinds, `e.${branch.joinKindField}`),
      );
    }

    if (branch.duplicateGuard !== undefined) {
      recursiveFilterClauses.push(branch.duplicateGuard);
    }

    const recursiveSelectColumns = [
      ...startColumnsFromRecursive,
      ...nodeColumnsFromRecursive,
      sql`r.depth + 1 AS depth`,
    ];
    if (compiledStopExpression !== undefined) {
      recursiveSelectColumns.push(
        sql`COALESCE(${compiledStopExpression}, ${dialect.booleanLiteral(false)}) AS stop_reached`,
      );
    }
    if (pathExtension !== undefined) {
      recursiveSelectColumns.push(sql`${pathExtension} AS path`);
    }
    if (vl.pathFormat === "qualified") {
      const qualifiedPath = dialect.appendTextJsonArray(sql`r.qualified_path`, [
        sql`e.kind`,
        sql`e.id`,
        sql`${branch.joinField === "from_id" ? "out" : "in"}`,
        sql`n.kind`,
        sql`n.id`,
      ]);
      recursiveSelectColumns.push(sql`${qualifiedPath} AS qualified_path`);
    }
    const recursiveJoinClauses = compileWorktableJoinClauses(branch);
    const frontierJoin = identityFrontierExpansion?.frontierJoin ?? sql``;

    if (forceWorktableOuterJoinOrder) {
      const recursiveWhereClauses = [
        ...recursiveJoinClauses,
        ...recursiveFilterClauses,
      ];

      return sql`
        SELECT ${sql.join(recursiveSelectColumns, sql`, `)}
        FROM recursive_cte r
        ${frontierJoin}
        CROSS JOIN ${ctx.schema.edgesTable} e
        JOIN ${ctx.schema.nodesTable} n ON n.graph_id = e.graph_id
          AND n.id = e.${sql.raw(branch.targetField)}
          AND n.kind = e.${sql.raw(branch.targetKindField)}
        WHERE ${sql.join(recursiveWhereClauses, sql` AND `)}
      `;
    }

    return sql`
      SELECT ${sql.join(recursiveSelectColumns, sql`, `)}
      FROM recursive_cte r
      ${frontierJoin}
      JOIN ${ctx.schema.edgesTable} e ON ${sql.join(recursiveJoinClauses, sql` AND `)}
      JOIN ${ctx.schema.nodesTable} n ON n.graph_id = e.graph_id
        AND n.id = e.${sql.raw(branch.targetField)}
        AND n.kind = e.${sql.raw(branch.targetKindField)}
      WHERE ${sql.join(recursiveFilterClauses, sql` AND `)}
    `;
  }

  const directJoinField = direction === "out" ? "from_id" : "to_id";
  const directTargetField = direction === "out" ? "to_id" : "from_id";
  const directJoinKindField = direction === "out" ? "from_kind" : "to_kind";
  const directTargetKindField = direction === "out" ? "to_kind" : "from_kind";

  const directBranch = compileRecursiveBranch({
    joinField: directJoinField,
    targetField: directTargetField,
    joinKindField: directJoinKindField,
    targetKindField: directTargetKindField,
    edgeKinds: directEdgeKinds,
  });

  function compileInverseRecursiveBranch(): SqlFragment {
    const inverseJoinField = direction === "out" ? "to_id" : "from_id";
    const inverseTargetField = direction === "out" ? "from_id" : "to_id";
    const inverseJoinKindField = direction === "out" ? "to_kind" : "from_kind";
    const inverseTargetKindField =
      direction === "out" ? "from_kind" : "to_kind";
    const duplicateGuard = compileInverseTraversalDuplicateGuard(
      directEdgeKinds,
      inverseEdgeKinds,
      (overlappingKinds) => compileKindFilter(overlappingKinds, "e.kind"),
    );

    const inverseBranch = compileRecursiveBranch({
      joinField: inverseJoinField,
      targetField: inverseTargetField,
      joinKindField: inverseJoinKindField,
      targetKindField: inverseTargetKindField,
      edgeKinds: inverseEdgeKinds,
      duplicateGuard,
    });

    return sql`
      ${directBranch}
      UNION ALL
      ${inverseBranch}
    `;
  }

  const recursiveBranchSql =
    inverseEdgeKinds.length === 0 ?
      directBranch
    : compileInverseRecursiveBranch();
  const baseSelectColumns = [
    ...startColumnsFromBase,
    ...nodeColumnsFromBase,
    sql`0 AS depth`,
  ];
  if (compiledBaseStopExpression !== undefined) {
    baseSelectColumns.push(
      sql`COALESCE(${compiledBaseStopExpression}, ${dialect.booleanLiteral(false)}) AS stop_reached`,
    );
  }
  if (initialPath !== undefined) {
    baseSelectColumns.push(sql`${initialPath} AS path`);
  }

  if (vl.pathFormat === "qualified") {
    baseSelectColumns.push(
      sql`${dialect.textJsonArray([sql`n0.kind`, sql`n0.id`])} AS qualified_path`,
    );
  }

  return sql`
    recursive_cte AS (
      -- Base case: starting nodes
      SELECT ${sql.join(baseSelectColumns, sql`, `)}
      FROM ${ctx.schema.nodesTable} n0
      WHERE ${sql.join(baseWhereClauses, sql` AND `)}

      UNION ALL

      -- Recursive case: follow edges
      ${recursiveBranchSql}
    )
  `;
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Compiles a kind filter for IN clause.
 * Delegates to the shared compileKindFilter from predicate-utils
 * with a raw SQL column expression.
 */
function compileKindFilter(
  kinds: readonly string[],
  columnExpr: string,
): SqlFragment {
  return sharedCompileKindFilter(sql.raw(columnExpr), kinds);
}

/**
 * Compiles node predicates for a specific alias.
 * Filters by alias and excludes edge predicates (targetType !== "edge").
 */
function compileNodePredicates(
  ast: QueryAst,
  alias: string,
  ctx: PredicateCompilerContext,
): SqlFragment[] {
  return ast.predicates
    .filter((p) => p.targetAlias === alias && p.targetType !== "edge")
    .map((p) => compilePredicateExpression(p.expression, ctx));
}

/**
 * Compiles edge predicates for a specific edge alias.
 * Filters by alias and only includes edge predicates (targetType === "edge").
 */
function compileEdgePredicates(
  ast: QueryAst,
  edgeAlias: string,
  ctx: PredicateCompilerContext,
): SqlFragment[] {
  return ast.predicates
    .filter((p) => p.targetAlias === edgeAlias && p.targetType === "edge")
    .map((p) => compilePredicateExpression(p.expression, ctx));
}

function collectRequiredColumnsByAlias(
  ast: QueryAst,
  traversal: VariableLengthTraversal,
): RequiredColumnsByAlias | undefined {
  const selectiveFields = ast.selectiveFields;
  if (selectiveFields === undefined || selectiveFields.length === 0) {
    return undefined;
  }

  const requiredColumnsByAlias = new Map<string, Set<string>>();
  const previousNodeKinds = [
    ...new Set([...ast.start.kinds, ...traversal.nodeKinds]),
  ];

  // Recursive expansion always needs node alias id for joins/cycle checks.
  addRequiredColumn(requiredColumnsByAlias, traversal.nodeAlias, "id");
  if (previousNodeKinds.length > 1) {
    addRequiredColumn(requiredColumnsByAlias, traversal.nodeAlias, "kind");
  }

  for (const field of selectiveFields) {
    markSelectiveFieldAsRequired(requiredColumnsByAlias, field);
  }

  const edgeAliases = new Set(ast.traversals.map((item) => item.edgeAlias));
  for (const projected of ast.projection.fields) {
    const source = projected.source;
    if (source.__type === "aggregate") {
      if (!edgeAliases.has(source.field.alias)) {
        markFieldRefAsRequired(requiredColumnsByAlias, source.field);
      }
    } else if (source.__type === "database_expression") {
      visitExpressionFields(source, (field) => {
        if (!edgeAliases.has(field.alias)) {
          markFieldRefAsRequired(requiredColumnsByAlias, field);
        }
      });
    } else if (!edgeAliases.has(source.alias)) {
      markFieldRefAsRequired(requiredColumnsByAlias, source);
    }
  }

  if (ast.orderBy) {
    for (const orderSpec of ast.orderBy) {
      if (orderSpec.field.__type === "field_ref") {
        markFieldRefAsRequired(requiredColumnsByAlias, orderSpec.field);
      } else {
        visitExpressionFields(orderSpec.field, (field) => {
          markFieldRefAsRequired(requiredColumnsByAlias, field);
        });
      }
    }
  }

  if (ast.resultPredicate !== undefined)
    markPredicateFieldsAsRequired(requiredColumnsByAlias, ast.resultPredicate);

  return requiredColumnsByAlias;
}

function compileNodeSelectColumnsFromTable(
  tableAlias: string,
  alias: string,
  requiredColumns: ReadonlySet<string> | undefined,
  alwaysRequiredColumns: ReadonlySet<string>,
): SqlFragment[] {
  return NODE_COLUMNS.filter((column) =>
    shouldProjectColumn(requiredColumns, column, alwaysRequiredColumns),
  ).map(
    (column) =>
      sql`${sql.raw(tableAlias)}.${sql.raw(column)} AS ${sql.raw(`${alias}_${column}`)}`,
  );
}

function compileNodeSelectColumnsFromRecursiveRow(
  alias: string,
  requiredColumns: ReadonlySet<string> | undefined,
  alwaysRequiredColumns: ReadonlySet<string>,
): SqlFragment[] {
  return NODE_COLUMNS.filter((column) =>
    shouldProjectColumn(requiredColumns, column, alwaysRequiredColumns),
  ).map((column) => {
    const projected = `${alias}_${column}`;
    return sql`r.${sql.raw(projected)} AS ${sql.raw(projected)}`;
  });
}

/**
 * Compiles projection for recursive query results.
 */
function compileRecursiveProjection(
  ast: QueryAst,
  traversal: VariableLengthTraversal,
  ctx: PredicateCompilerContext,
): SqlFragment {
  if (ast.selectiveFields && ast.selectiveFields.length > 0) {
    return compileRecursiveSelectiveProjection(
      ast.selectiveFields,
      ast,
      traversal,
      ctx,
    );
  }

  const startAlias = ast.start.alias;
  const nodeAlias = traversal.nodeAlias;
  const vl = traversal.variableLength;

  const explicitProjection = hasExplicitRecursiveProjection(ast);
  const fields: SqlFragment[] =
    explicitProjection ?
      compileAdditionalRecursiveProjectionFields(
        ast,
        new Set(),
        ctx,
        undefined,
        new Set([traversal.edgeAlias]),
      )
    : [startAlias, nodeAlias].flatMap((alias) =>
        NODE_COLUMNS.map((column) => {
          const name = `${alias}_${column}`;
          return sql`${sql.raw(name)} AS ${quoteIdentifier(name)}`;
        }),
      );

  if (vl.depthAlias !== undefined) {
    fields.push(sql`depth AS ${quoteIdentifier(vl.depthAlias)}`);
  }

  if (vl.pathAlias !== undefined) {
    fields.push(
      sql`${sql.raw(vl.pathFormat === "qualified" ? "qualified_path" : "path")} AS ${quoteIdentifier(vl.pathAlias)}`,
    );
  }

  if (!explicitProjection)
    fields.push(
      ...compileAdditionalRecursiveProjectionFields(
        ast,
        new Set(
          [startAlias, nodeAlias].flatMap((alias) =>
            NODE_COLUMNS.map((column) => `${alias}_${column}`),
          ),
        ),
        ctx,
        undefined,
        new Set([traversal.edgeAlias]),
      ),
    );

  return sql.join(fields, sql`, `);
}

function compileRecursiveSelectiveProjection(
  fields: readonly SelectiveField[],
  ast: QueryAst,
  traversal: VariableLengthTraversal,
  ctx: PredicateCompilerContext,
): SqlFragment {
  const dialect = ctx.dialect;
  const allowedAliases = new Set([ast.start.alias, traversal.nodeAlias]);

  const columns = fields.map((field) =>
    compileRecursiveSelectiveField(field, allowedAliases, dialect),
  );

  columns.push(
    ...compileAdditionalRecursiveProjectionFields(
      ast,
      new Set(fields.map((field) => field.outputName)),
      ctx,
      undefined,
      new Set([traversal.edgeAlias]),
    ),
  );

  // Include recursive depth/path columns when present
  const vl = traversal.variableLength;
  if (vl.depthAlias !== undefined) {
    columns.push(sql`depth AS ${quoteIdentifier(vl.depthAlias)}`);
  }
  if (vl.pathAlias !== undefined) {
    columns.push(
      sql`${sql.raw(vl.pathFormat === "qualified" ? "qualified_path" : "path")} AS ${quoteIdentifier(vl.pathAlias)}`,
    );
  }

  return sql.join(columns, sql`, `);
}

/** Explicit SQL projections choose their output shape; compatibility select() hydrates aliases. */
export function hasExplicitRecursiveProjection(ast: QueryAst): boolean {
  return ast.projection.fields.some(
    (field) => field.source.__type !== "field_ref",
  );
}

/** Compiles a decoded node field consistently in single and composed recursion. */
export function compileRecursiveSelectiveField(
  field: SelectiveField,
  allowedAliases: ReadonlySet<string>,
  dialect: DialectAdapter,
  resultAlias?: string,
): SqlFragment {
  if (!allowedAliases.has(field.alias))
    throw new UnsupportedPredicateError(
      `Selective projection for recursive traversals does not support alias "${field.alias}"`,
    );
  const column = `${field.alias}_${field.isSystemField ? mapSelectiveSystemFieldToColumn(field.field) : "props"}`;
  const reference =
    resultAlias === undefined ?
      sql.raw(column)
    : sql`${sql.identifier(resultAlias)}.${sql.identifier(column)}`;
  const value =
    field.isSystemField ? reference : (
      compileSelectivePropsExtraction(field, reference, dialect)
    );
  return sql`${value} AS ${quoteIdentifier(field.outputName)}`;
}

/** Emits envelope-only projection fields that selective decoding did not request. */
export function compileAdditionalRecursiveProjectionFields(
  ast: QueryAst,
  emittedOutputNames: ReadonlySet<string>,
  ctx: PredicateCompilerContext,
  resultAlias?: string,
  edgeAliases: ReadonlySet<string> = new Set(),
): SqlFragment[] {
  return ast.projection.fields
    .filter((field) => !emittedOutputNames.has(field.outputName))
    .map((field) => {
      const source = field.source;
      const sourceAlias =
        source.__type === "aggregate" ? source.field.alias
        : source.__type === "database_expression" ? undefined
        : source.alias;
      if (source.__type === "database_expression") {
        visitExpressionFields(source, (reference) => {
          if (edgeAliases.has(reference.alias))
            throw new UnsupportedPredicateError(
              "Scalar recursive-edge projection is not supported; request a qualified path instead.",
            );
        });
      }
      if (sourceAlias !== undefined && edgeAliases.has(sourceAlias)) {
        // Compatibility select() carries structural edge columns even when the
        // callback never reads an edge. Only these synthetic columns are empty.
        if (field.cteAlias !== undefined)
          return sql`NULL AS ${quoteIdentifier(field.outputName)}`;
        throw new UnsupportedPredicateError(
          "Scalar recursive-edge projection is not supported; request a qualified path instead.",
        );
      }
      if (source.__type === "aggregate") {
        throw new UnsupportedPredicateError(
          "Aggregate projection is not supported for recursive traversals",
        );
      }
      const value =
        source.__type === "database_expression" ?
          compileRecursiveDatabaseExpression(
            source,
            ctx,
            resultAlias,
            "recursive projection",
          )
        : resultAlias === undefined ?
          compileFieldValue(source, ctx.dialect, source.valueType)
        : compileRecursiveResultField(source, ctx.dialect, resultAlias);
      return sql`${value} AS ${quoteIdentifier(field.outputName)}`;
    });
}

function compileRecursiveDatabaseExpression(
  expression: DatabaseExpression,
  ctx: PredicateCompilerContext,
  resultAlias: string | undefined,
  clause: string,
): SqlFragment {
  return compileDatabaseExpression(expression, {
    dialect: ctx.dialect,
    allowAggregates: false,
    aggregateClause: clause,
    ...(ctx.compileExpressionSubquery === undefined ?
      {}
    : { compileSubquery: ctx.compileExpressionSubquery }),
    ...(ctx.compileExpressionOuterReference === undefined ?
      {}
    : { compileOuterReference: ctx.compileExpressionOuterReference }),
    ...(resultAlias === undefined ?
      {}
    : {
        compileFieldExpression(field, fieldExpression) {
          return compileRecursiveResultField(
            field,
            ctx.dialect,
            resultAlias,
            fieldExpression.valueType,
          );
        },
      }),
  });
}

export function compileRecursiveResultField(
  field: FieldRef,
  dialect: DialectAdapter,
  resultAlias: string,
  valueType = field.valueType,
): SqlFragment {
  const baseColumn =
    field.path[0] === "props" ?
      `${field.alias}_props`
    : `${field.alias}_${field.path.join("_")}`;
  const column = sql`${sql.identifier(resultAlias)}.${sql.identifier(baseColumn)}`;
  return compileFieldValueFromColumn(field, dialect, valueType, column);
}

/**
 * Compiles ORDER BY for recursive query.
 */
export function compileRecursiveOrderBy(
  ast: QueryAst,
  ctx: PredicateCompilerContext,
  resultAlias?: string,
): SqlFragment | undefined {
  if (!ast.orderBy || ast.orderBy.length === 0) {
    return undefined;
  }

  const parts: SqlFragment[] = [];

  for (const orderSpec of ast.orderBy) {
    const valueType = orderSpec.field.valueType;
    if (valueType === "array" || valueType === "object") {
      throw new UnsupportedPredicateError(
        "Ordering by JSON arrays or objects is not supported",
      );
    }
    const field =
      orderSpec.field.__type === "database_expression" ?
        compileRecursiveDatabaseExpression(
          orderSpec.field,
          ctx,
          resultAlias,
          "recursive ORDER BY",
        )
      : resultAlias === undefined ?
        compileFieldValue(orderSpec.field, ctx.dialect, valueType)
      : compileRecursiveResultField(
          orderSpec.field,
          ctx.dialect,
          resultAlias,
          valueType,
        );
    const nulls = resolveNullOrdering(orderSpec);
    parts.push(compileOrderTerm(field, orderSpec.direction, nulls));
  }

  return sql`ORDER BY ${sql.join(parts, sql`, `)}`;
}

/**
 * Compiles LIMIT and OFFSET clauses.
 */
function compileLimitOffset(
  ast: QueryAst,
  dialect: DialectAdapter,
): SqlFragment | undefined {
  const parts = compileLimitOffsetClauses(ast.limit, ast.offset, dialect);
  return parts.length > 0 ? sql.join(parts, sql` `) : undefined;
}
